import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOfflineRuntimeIfAvailable } from '@floorfile/offline'
import type { AttachmentContext } from '@floorfile/contracts'
import { useAuth } from '@/lib/auth'
import { objectFetch, sha256Base64 } from '@/lib/object-fetch'
import { useCompanyConfig } from '@/lib/company-config'
import { parseApiResponse } from '@/lib/api-response'
import { assertUploadable, declaredMimeType } from './upload-errors'

/**
 * The shared attachment client (attachment-picker pass): one hook for the
 * create → PUT bytes → confirm protocol (SYSTEM_ARCHITECTURE §7), one for
 * listing an entity's stored files, one for authenticated downloads.
 * Composers upload against a client-side DRAFT entity id — the owning
 * record claims the rows at create time (chat/comments) or the id is
 * passed in an input column (training certificates, form values).
 *
 * OFFLINE (D24 §6): when the network is away, the captured Blob lands in
 * the device outbox under a client-minted attachment id — the parent
 * record references that id immediately and the upload replays FIRST on
 * reconnect (dependency ordering). Every picker/signature pad gets this
 * for free by riding this one chokepoint.
 */

export interface UploadedAttachment {
  id: string
  fileName: string
  mimeType: string
  sizeBytes: number
  /** Omitted by the server when GENERAL (UI plan Phase 2). */
  context?: AttachmentContext | undefined
  /** Set on an annotation derivative; names the untouched original. */
  derivedFromAttachmentId?: string | undefined
  uploadedBy?: string | undefined
  uploadedByName?: string | undefined
  createdAt?: string | undefined
}

export interface AttachmentTarget {
  entityType: string
  entityId: string
  module: string
}

export function draftEntityId(): string {
  return crypto.randomUUID()
}

export function useUploadAttachment() {
  const { authFetch } = useAuth()
  const queryClient = useQueryClient()
  const offline = useOfflineRuntimeIfAvailable()
  const companyConfig = useCompanyConfig()
  return useMutation({
    /**
     * F5.2: the mutation decides offline for ITSELF (the outbox branch below).
     * TanStack's default `online` mode PAUSED it whenever the browser reported
     * no network — airplane mode, a phone with Wi-Fi off — so the capture never
     * started and the camera button spun forever; the dead-zone branch only
     * ever ran when the device still believed it was online.
     */
    networkMode: 'always',
    mutationFn: async (input: {
      file: File | Blob
      target: AttachmentTarget
      /** Required when uploading a Blob (canvas signatures). */
      fileName?: string
      /** Where in the workflow this was captured (UI plan Phase 2). */
      context?: AttachmentContext
      /** Set by the annotation editor — the ORIGINAL is never overwritten. */
      derivedFromAttachmentId?: string
    }): Promise<UploadedAttachment> => {
      if (!companyConfig.uploadsEnabled) throw new Error('File uploads are disabled for this company.')
      const fileName =
        input.fileName ?? (input.file instanceof File ? input.file.name : 'upload.bin')
      const mimeType = input.file.type || 'application/octet-stream'
      // F32: refuse what the server would refuse, BEFORE any request, with a
      // reason the dialog can name (an empty file, too big, a bad type).
      assertUploadable({ size: input.file.size, name: fileName, type: declaredMimeType({ type: mimeType }) })

      const captureOffline = async (): Promise<UploadedAttachment> => {
        if (!offline) throw new Error('Upload failed — no connection.')
        const { attachmentId } = await offline.captureAttachment({
          blob: input.file,
          fileName,
          module: input.target.module,
          targetEntityType: input.target.entityType,
          targetEntityId: input.target.entityId,
          ...(input.context ? { context: input.context } : {}),
          ...(input.derivedFromAttachmentId ? { derivedFromAttachmentId: input.derivedFromAttachmentId } : {}),
        })
        return { id: attachmentId, fileName, mimeType, sizeBytes: input.file.size }
      }

      if (offline?.isOffline) return captureOffline()

      // Audit FF-08: hash ONCE, before registration, and reuse the same buffer
      // for the PUT — so the header can never disagree with what was signed.
      // `undefined` (no WebCrypto / insecure context) falls back to the old
      // flow exactly: no declaration, no signed header, upload unchanged.
      const bytes = await input.file.arrayBuffer()
      const checksumSha256 = await sha256Base64(bytes)

      let metaRes: Response
      try {
        metaRes = await authFetch('/api/v1/attachments', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            entityType: input.target.entityType,
            entityId: input.target.entityId,
            module: input.target.module,
            fileName,
            mimeType,
            sizeBytes: input.file.size,
            ...(checksumSha256 ? { checksumSha256 } : {}),
            ...(input.context ? { context: input.context } : {}),
            ...(input.derivedFromAttachmentId
              ? { derivedFromAttachmentId: input.derivedFromAttachmentId }
              : {}),
          }),
        })
      } catch {
        // Network died mid-action (D24 §8): capture locally instead.
        offline?.reachability.noteFailure()
        return captureOffline()
      }
      // F32: the parsed ApiError keeps `code` and `details` — a rebuilt
      // `Error(message)` threw away the field that says what was wrong.
      const meta = await parseApiResponse<{ data: { attachmentId: string; uploadUrl: string } }>(metaRes)

      const putRes = await objectFetch(authFetch, meta.data.uploadUrl, {
        method: 'PUT',
        headers: {
          'content-type': 'application/octet-stream',
          // MUST be byte-identical to what the server signed (audit FF-08).
          ...(checksumSha256 ? { 'x-amz-checksum-sha256': checksumSha256 } : {}),
        },
        body: bytes,
      })
      if (!putRes.ok) throw new Error('File upload failed.')
      // A failed confirm is a failed upload — never hand back an unconfirmed id.
      await parseApiResponse(await authFetch(`/api/v1/attachments/${meta.data.attachmentId}/confirm`, { method: 'POST' }))

      return {
        id: meta.data.attachmentId,
        fileName,
        mimeType,
        sizeBytes: input.file.size,
        ...(input.context ? { context: input.context } : {}),
        ...(input.derivedFromAttachmentId
          ? { derivedFromAttachmentId: input.derivedFromAttachmentId }
          : {}),
      }
    },
    onSuccess: (_attachment, input) => void queryClient.invalidateQueries({ queryKey: ['attachments', input.target.entityType, input.target.entityId] }),
  })
}

export function useEntityAttachments(entityType: string, entityId: string | undefined) {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['attachments', entityType, entityId],
    enabled: !!entityId,
    queryFn: async () => {
      const res = await authFetch(
        `/api/v1/attachments?entityType=${encodeURIComponent(entityType)}&entityId=${entityId}`,
      )
      if (!res.ok) throw new Error('Could not load attachments.')
      return (await res.json()) as { data: UploadedAttachment[] }
    },
  })
}

/**
 * Blob URLs for a set of private attachments (UI plan Phase 2).
 *
 * Attachments are permission-checked R2 objects, so an `<img src>` cannot
 * carry the caller's credential — every byte has to be fetched with
 * `authFetch` and turned into an object URL. `AuthenticatedAttachmentImage`
 * does that for ONE image; a gallery needs many, and one component per photo
 * would re-fetch on every parent re-render and leak a URL each time.
 *
 * Fetches are capped and sequential-ish by design: a work order with thirty
 * photos should not open thirty concurrent authenticated requests the moment
 * a technician taps the tab.
 *
 * Every created URL is revoked on unmount and whenever an id leaves the set —
 * a blob URL that is never revoked pins its bytes in memory for the life of
 * the document, which on a shared tablet is the whole shift.
 */
export function useAttachmentObjectUrls(ids: readonly string[]): Record<string, string> {
  const { authFetch } = useAuth()
  const [urls, setUrls] = useState<Record<string, string>>({})
  const createdRef = useRef(new Map<string, string>())
  // The ids array is a fresh literal on every render; the JOINED key is what
  // actually changes, so the effect does not restart on each keystroke
  // elsewhere on the page.
  const key = ids.join(',')

  useEffect(() => {
    let active = true
    const wanted = key ? key.split(',') : []
    const created = createdRef.current
    const publish = () => setUrls(Object.fromEntries(created))

    // Drop what is no longer wanted before fetching what is.
    for (const [id, url] of [...created]) {
      if (!wanted.includes(id)) {
        URL.revokeObjectURL(url)
        created.delete(id)
      }
    }

    void (async () => {
      for (const id of wanted) {
        if (!active || created.has(id)) continue
        try {
          const res = await authFetch(`/api/v1/attachments/${id}/content`)
          if (!res.ok || !active) continue
          const url = URL.createObjectURL(await res.blob())
          if (!active) {
            URL.revokeObjectURL(url)
            return
          }
          created.set(id, url)
          publish()
        } catch {
          // A single unreadable file must not blank the whole gallery.
        }
      }
      if (active) publish()
    })()

    return () => {
      active = false
    }
  }, [authFetch, key])

  // Unmount-only revoke. Kept separate from the effect above so a changing id
  // list does not tear down URLs that are still on screen.
  useEffect(() => {
    const created = createdRef.current
    return () => {
      for (const url of created.values()) URL.revokeObjectURL(url)
    }
  }, [])

  return urls
}

/** Authenticated download → browser save (the documents-page pattern). Goes
 * via download-url so a presigned GET (when configured) streams straight from
 * R2, bypassing the Worker; falls back to the proxied content path locally. */
export function useDownloadAttachment() {
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({ id, fileName }: { id: string; fileName: string }) => {
      const linkRes = await authFetch(`/api/v1/attachments/${id}/download-url`)
      if (!linkRes.ok) throw new Error('Download failed.')
      const { data } = (await linkRes.json()) as { data: { url: string } }
      const res = await objectFetch(authFetch, data.url)
      if (!res.ok) throw new Error('Download failed.')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      URL.revokeObjectURL(url)
    },
  })
}
